import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaTimes, FaTrophy, FaCrown } from "react-icons/fa";

export default function WinnerAnnouncementModal({ autoCloseMs = 5000 }) {
  const [open, setOpen] = useState(false);
  const [winners, setWinners] = useState([]);

  useEffect(() => {
    setOpen(true);
    const t = setTimeout(() => setOpen(false), autoCloseMs);
    return () => clearTimeout(t);
  }, [autoCloseMs]);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/top-rankers`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (data?.success && Array.isArray(data.users)) {
          setWinners(data.users.slice(0, 3));
        }
      } catch (err) {
        console.error("Failed to load winners", err);
      }
    };
    fetchWinners();
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[9999] bg-[#020617] backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
          >
            <div className="relative w-full max-w-md overflow-hidden rounded-[32px]
bg-gradient-to-br from-[#081120] via-[#0B172A] to-[#132238]
border border-amber-400/20
shadow-[0_30px_80px_rgba(0,0,0,.65)]">
              
              {/* Top Accent Bar */}
        <div className="absolute inset-x-0 top-0 h-[2px]
bg-gradient-to-r
from-transparent
via-amber-400
to-transparent" />

              <button
                onClick={() => setOpen(false)}
      className="absolute top-4 right-4 text-slate-300 hover:text-slate-600 transition-colors"
              >
                <FaTimes size={14} />
              </button>

              <div className="p-5">
                {/* Header Area */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center
gap-2
px-4
py-2
rounded-full
bg-amber-500/10
border border-amber-400/30
text-amber-300
text-[10px]
font-black
uppercase
tracking-[0.25em]">
                    <FaTrophy /> Hall of Fame
                  </div>
                  <h3 className="mt-3
text-3xl
font-black
text-white
tracking-wide">Weekly Elite</h3>
                </div>

                {/* Compact Reward Display */}
                <div className="relative
flex
justify-center
items-end
gap-5
mb-8">
                  <motion.img
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2.5 }}
                    src="/image/iphone.png"
                    alt="iPhone"
                   className="w-24
drop-shadow-[0_15px_30px_rgba(251,191,36,.35)]
hover:scale-110
transition"
                  />
                  <motion.img
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 2.5, delay: 0.3 }}
                    src="/image/iphone17.png"
                    alt="iPhone"
                    className="w-16 drop-shadow-xl"
                  />
                </div>

                {/* Winners List - Compact */}
                <div className="space-y-2">
                  {winners.map((u, idx) => (
                    <div
                      key={u._id || idx}
               className="
group
flex
items-center
justify-between
p-4
rounded-2xl
bg-white/5
border
border-white/10
backdrop-blur-xl
hover:border-amber-400/40
hover:bg-white/10
transition-all
duration-300"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-black ${
                         idx===0
? 'bg-gradient-to-br from-yellow-300 to-amber-500 text-black shadow-lg shadow-yellow-500/30'

: idx===1

? 'bg-gradient-to-br from-slate-200 to-slate-400 text-black'

: 'bg-gradient-to-br from-orange-400 to-orange-700 text-white'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <div className="
text-sm
font-extrabold
text-white
truncate
flex
items-center
gap-2">
                            {idx === 0 && <FaCrown className="text-yellow-400 text-sm" />}
                            {u.name}
                          </div>
                          <p className="text-[9px] font-bold text-amber-300
 uppercase tracking-tighter">
                            ${Math.round(u.totalEarnings).toLocaleString()} Yield
                          </p>
                        </div>
                      </div>
                      <div className="
px-3
py-1.5
rounded-full
bg-amber-500/15
border
border-amber-400/30
text-amber-300
text-[10px]
font-black
uppercase">
                        Winner
                      </div>
                    </div>
                  ))}
                  
                  {!winners.length && (
                    <div className="
text-center
py-8
text-amber-300
uppercase
font-bold
tracking-[0.25em]
animate-pulse">
                      Updating Leaders...
                    </div>
                  )}
                </div>

                {/* Progress Mini Info */}
                <div className="
mt-8
pt-6
border-t
border-white/10
flex
items-center
justify-between">
                  <div className="
text-[10px]
font-bold
text-slate-400
uppercase
tracking-[0.25em]">
                    Next Sync in {autoCloseMs / 1000}sec
                  </div>
                  <div className="flex gap-1">
                    <span className="w-1 h-1 rounded-full bg-blue-400 animate-bounce" />
                    <span className="w-1 h-1 rounded-full bg-blue-300 animate-bounce [animation-delay:0.2s]" />
                    <span className="w-1 h-1 rounded-full bg-blue-400 animate-bounce [animation-delay:0.4s]" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}